import { apiDelete, apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';
import {
  entityDetailKey,
  entityListKey,
  type EntityListKey,
} from '../../../shared/api/entityQueryKeys.js';

export interface ContactDealSummary {
  id: string;
  title: string;
  stage: string;
  amount: number | string;
  currency: string;
}

export interface ContactInteractionItem {
  id: string;
  type: string;
  subject?: string | null;
  body?: string | null;
  channel?: string | null;
  occurredAt: string;
}

export interface ContactItem {
  id: string;
  workspaceId: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  source?: string | null;
  role?: string | null;
  /** Legacy-строка (свободный ввод). Каноническая связь — companyId/companyRef. */
  company?: string | null;
  companyId?: string | null;
  companyRef?: { id: string; name: string } | null;
  tags: string[];
  notes?: string | null;
  deals: ContactDealSummary[];
  interactions: ContactInteractionItem[];
  createdAt: string;
  updatedAt: string;
  fullName: string;
  dealsCount: number;
}

export interface ListContactsFilters {
  q?: string;
  companyId?: string;
  /** Один тег контакта. */
  tag?: string;
  /** `deals` — со сделками, `email` — с заполненным email. */
  has?: 'deals' | 'email';
}

export interface CreateContactInput {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyId?: string | null;
  tags?: string[];
}

export interface UpdateContactInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyId?: string | null;
  tags?: string[];
  expectedUpdatedAt?: string;
}

function enhanceContact(contact: Omit<ContactItem, 'fullName' | 'dealsCount'>): ContactItem {
  return {
    ...contact,
    fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' '),
    dealsCount: contact.deals.length,
  };
}

export function getContactsQueryKey(
  workspaceId: string,
  filters: ListContactsFilters = {},
): EntityListKey {
  return entityListKey('contacts', workspaceId, { ...filters });
}

export function getContactDetailQueryKey(
  workspaceId: string,
  contactId: string,
): readonly string[] {
  // Тот же корень, что и у списка: инвалидация префикса обновляет и карточку.
  return entityDetailKey('contacts', workspaceId, contactId);
}

export async function listContacts(filters: ListContactsFilters = {}): Promise<ContactItem[]> {
  const search = new URLSearchParams();
  if (filters.q?.trim()) {
    search.set('q', filters.q.trim());
  }
  if (filters.companyId) {
    search.set('companyId', filters.companyId);
  }
  if (filters.tag) {
    search.set('tag', filters.tag);
  }
  if (filters.has) {
    search.set('has', filters.has);
  }

  const path = search.size > 0 ? `/contacts?${search.toString()}` : '/contacts';
  const contacts = await apiGet<Array<Omit<ContactItem, 'fullName' | 'dealsCount'>>>(path);
  return contacts.map(enhanceContact);
}

export async function getContactById(contactId: string): Promise<ContactItem> {
  const contact = await apiGet<Omit<ContactItem, 'fullName' | 'dealsCount'>>(
    `/contacts/${contactId}`,
  );
  return enhanceContact(contact);
}

export async function createContact(input: CreateContactInput): Promise<ContactItem> {
  const contact = await apiPost<Omit<ContactItem, 'fullName' | 'dealsCount'>, CreateContactInput>(
    '/contacts',
    input,
  );
  return enhanceContact(contact);
}

export async function updateContact(
  contactId: string,
  input: UpdateContactInput,
): Promise<ContactItem> {
  const contact = await apiPatch<Omit<ContactItem, 'fullName' | 'dealsCount'>, UpdateContactInput>(
    `/contacts/${contactId}`,
    input,
  );
  return enhanceContact(contact);
}

export type InteractionType = 'call' | 'email' | 'meeting' | 'note' | 'sms' | 'in_app';

export interface CreateInteractionInput {
  type: InteractionType;
  subject?: string;
  body?: string;
  channel?: string;
  occurredAt?: string;
}

export function getContactInteractionsQueryKey(
  workspaceId: string,
  contactId: string,
): readonly string[] {
  return entityDetailKey('contacts', workspaceId, contactId, 'interactions');
}

export async function listContactInteractions(
  contactId: string,
): Promise<ContactInteractionItem[]> {
  return apiGet<ContactInteractionItem[]>(`/contacts/${contactId}/interactions`);
}

export async function createContactInteraction(
  contactId: string,
  input: CreateInteractionInput,
): Promise<ContactInteractionItem> {
  return apiPost<ContactInteractionItem, CreateInteractionInput>(
    `/contacts/${contactId}/interactions`,
    input,
  );
}

export interface UpdateInteractionInput {
  type?: InteractionType;
  subject?: string | null;
  body?: string | null;
  channel?: string | null;
  occurredAt?: string;
}

export async function updateContactInteraction(
  contactId: string,
  interactionId: string,
  input: UpdateInteractionInput,
): Promise<ContactInteractionItem> {
  return apiPatch<ContactInteractionItem, UpdateInteractionInput>(
    `/contacts/${contactId}/interactions/${interactionId}`,
    input,
  );
}

export async function deleteContactInteraction(
  contactId: string,
  interactionId: string,
): Promise<void> {
  await apiDelete<void>(`/contacts/${contactId}/interactions/${interactionId}`);
}

export async function deleteContact(contactId: string): Promise<void> {
  await apiDelete<void>(`/contacts/${contactId}`);
}
